import { getCenter } from 'ol/extent';
import { boundingExtent } from 'ol/extent';
import { csv as d3csv } from 'd3';
import { DevDataService } from './DevDataService';
import { statesList, countiesList } from './StaticVariables';
import { downloadString } from '../controller/DownloadString';
import { pick } from 'lodash';
import { fromLonLat, transformExtent, get as getExtent } from 'ol/proj';

// ΔVΔVΔ: Pass an SVI row return themes spport different data attributes for themes by year
const mapThemeAttributes = (obj) => {
    const thm0 = obj.R_PL_THEME1 || obj.RPL_THEMES || obj.Overall_SVI_Percentile;
    const thm1 = obj.R_PL_THEME2 || obj.RPL_THEME1 || obj.Theme1_Socioeconomic_Percentile || obj.Theme1_Percentile;
    const thm2 = obj.R_PL_THEME3 || obj.RPL_THEME2 || obj.Theme2_HouseholdAndDisability_Percentile || obj.Theme2_Percentile;
    const thm3 = obj.R_PL_THEME4 || obj.RPL_THEME3 || obj.Theme3_MinorityAndLanguage_Percentile || obj.Theme3_Percentile;
    const thm4 = obj.R_PL_THEME5 || obj.RPL_THEME4 || obj.Theme4_HousingAndTransportation_Percentile || obj.Theme4_Percentile;
    return {
        theme0: sviValueToSlug(thm0),
        theme1: sviValueToSlug(thm1),
        theme2: sviValueToSlug(thm2),
        theme3: sviValueToSlug(thm3),
        theme4: sviValueToSlug(thm4),
        theme0Num: thm0,
        theme1Num: thm1,
        theme2Num: thm2,
        theme3Num: thm3,
        theme4Num: thm4
    };
};
export { mapThemeAttributes };

function sviValueToSlug(num) {
    if (num < 0) return 'none';
    if (num > -1 && num < 0.25) return 'low';
    if (num > 0.24 && num < 0.5) return 'med';
    if (num > 0.49 && num < 0.75) return 'high';
    if (num > 0.74) return 'top';
    return 'none';
}
const formatSVIobj = async (arr) => {
    let list = {};
    arr.forEach((row) => {
        let themes = mapThemeAttributes(row);
        list[row.FIPS] = Object.values(themes);
    });
    return list;
};
export { formatSVIobj };

const getTractFill = async (props) => {
    let list = {};
    return await d3csv(props.styleFile).then((dat) => {
        dat.forEach((row) => {
            let themes = mapThemeAttributes(row);
            list[row.FIPS] = Object.values(themes);
        });
        return list;
    });
};
export { getTractFill };

const mapSVIJson = (dat) => {
    let list = {};
    dat.forEach((row) => {
        let themes = mapThemeAttributes(row);
        list[row.FIPS] = Object.values(themes);
    });
    return list;
};
export { mapSVIJson };

const convertCountyList = async (includeTerritories = false) => {
    const countyCoords = await d3csv('/data/list/county_coords.csv');
    const DS = new DevDataService();

    const countiesMapped = await DS.getListData('county').then((res) => {
        return res
            .filter((rec) => {
                return rec.SN != null;
            })
            .map((item, i) => {
                let { CC, CN, SN } = item;
                //ΔVΔVΔ: Map County coords
                let coord = countyCoords.find((record) => record.fips * 1 === CC * 1);
                if (!coord) coord = getStateBy(CC.substring(0, 2)); //Use state as a fallback
                let cen = coord?.lon ? coord.lon + ',' + coord.lat : coord.center;
                SN = coord ? coord.abbr : SN;
                return { SN, CC, CN, cen };
            });
    });
    return includeTerritories ? countiesMapped : countiesMapped.filter((cnt) => cnt.SN && cnt.SN !== 'PR');
};
export { convertCountyList };

//ΔVΔVΔ: For app cached data building
const convertCountyListToCSV = async (includeTerritories = false) => {
    const arr = await convertCountyList(includeTerritories);
    const link = jsonToCSVDownloadString(arr);
    downloadString(link, 'counties.csv');
};

export { convertCountyListToCSV };

//ΔVΔVΔ: Expocts fips to be obj {12345:true,65789:true}
const fipsToState = (fips) => {
    const stateIDS = {};
    Object.keys(fips).map((key) => {
        let id = key.substring(0, 2);
        stateIDS[id] = true;
    });

    return Object.keys(stateIDS)
        .filter((id) => id < 57 || id === 72)
        .map((id) => getStateBy(id));
};
export { fipsToState };

const getCountyBy = (value, field = 'id') => {
    // ΔVΔVΔ:  Search through static county list to find matching value
    return false; // if unfound
};

const getStateBy = (value, field = 'id') => {
    // ΔVΔVΔ:  Search through static state list to find matching value
    return statesList.find((st) => st[field] == value);
};
export { getStateBy };

//ΔVΔVΔ: Convert array of objects to csv string
const jsonToCSVString = (json, useQuotes = true) => {
    const jcopy = json.slice(0);
    const indexRow = Object.keys(jcopy[0]);

    jcopy.unshift(indexRow);
    const rows = jcopy.map((row) => {
        if (!useQuotes) return Object.values(row).join(',');
        return '"' + Object.values(row).join('","') + '"';
    });
    return rows.join('\n');
};
export { jsonToCSVString };

//ΔVΔVΔ: Convert csv obj to url encoded link
const jsonToCSVDownloadString = (json) => {
    let csvLink = 'data:text/csv;charset=utf-8,' + jsonToCSVString(json);
    return encodeURI(csvLink).replace(/#/g, '%23');
};
export { jsonToCSVDownloadString };

const jsonToDownloadString = (json) => {
    return 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(json));
};
export { jsonToDownloadString };

const convertStateList = async () => {
    const DS = new DevDataService();
    const latlon = await d3csv('/data/list/state-lon-lat.csv').then((json) => {
        return json;
    });
    return DS.getListData('state')
        .then((res) => {
            return res.map((item, i) => {
                let slug = item.STATE_NAME.toLowerCase().replace(/ /g, '');
                const { minx, miny, maxx, maxy, STATE_ABBR } = item;
                const findSt = latlon.find((st) => {
                    return st.state == item.STATE_NAME;
                });
                const center = findSt ? [findSt.lon * 1, findSt.lat * 1] : [0, 0];
                const bounds = [minx, miny, maxx, maxy];
                return { id: item.STATE_FIPS, label: item.STATE_NAME, bounds, slug, center, abbr: STATE_ABBR };
            });
        })
        .then((dat) => dat.slice(1)) // ΔVΔVΔ: Remove "All States"
        .then((dat) => console.log('**** ', dat));
};
export { convertStateList };

const sortAlphabetical = (a, b) => {
    let na = a.name;
    let nb = b.name;
    if (na < nb) return -1;
    if (na > nb) return 1;
    return 0;
};
// Math.abs
export { sortAlphabetical };

const createTestPoint = (top, left, str, id) => {
    // if (id > 100) return;
    // const prevdot = document.querySelectorAll('[pose="' + str + top + ',' + left + '"]');
    // if (prevdot.length) return document.createElement('div');
    const dot = document.createElement('div');
    dot.classList.add('testdot', str);
    dot.setAttribute('pose', str + top + ',' + left);
    // dot.id = str + id;
    dot.style.top = top + 'px';
    dot.style.left = left + 'px';
    return dot;
};
const addTestPoints = (a, rest, str = '') => {
    const grp = document.createElement('div');
    grp.classList.add('tstgrp', str);
    grp.id = 'grp' + document.querySelectorAll('.tstgrp').length;
    if (a) grp.appendChild(createTestPoint(a[1], a[0], 'a'));
    for (let b of rest) {
        grp.appendChild(createTestPoint(b[1], b[0], b.length == 3 ? 'c' : 'b'));
    }
    document.querySelector('.tabpanel #test').appendChild(grp);
};
export { addTestPoints };

const addTestPointInteractions = () => {
    document.querySelector('.tstgrp').classList.add('active'); //add class to first node
    document.querySelector('.tabpanel #test .tstgrp').addEventListener('mouseover', (e) => {
        let prevactive = document.querySelector('.tstgrp.active');
        if (prevactive) prevactive.classList.remove('active');
        let pos = e.target.getAttribute('pose').replace('b', 'a');
        let targetDot = document.querySelector(`[pose="${pos}"]`);
        if (targetDot?.parentNode) targetDot?.parentNode.classList.add('active');
    });
    document.querySelector('.tabpanel #test .tstgrp').addEventListener('mouseout', (e) => {
        let prevactive = document.querySelector('.tstgrp.active');
        if (prevactive) prevactive.classList.remove('active');
    });
};
export { addTestPointInteractions };

// Return boolean: check to see if 2 points are within specified range
const pointsAreWithinRange = (pointA, pointB, range) => {
    // use range * range is the same as using the sqrt of the distance calculation
    let d = distanceBetweenPoints(pointA, pointB, false);
    return d < range * range;
};
export { pointsAreWithinRange };

const distanceBetweenPoints = (pointA, pointB, absolute = false) => {
    let w = Math.abs(pointA[0] - pointB[0]); //x-x
    let l = Math.abs(pointA[1] - pointB[1]); //y-y
    let d = Math.floor(l * l + w * w);
    if (!absolute) return d; //Relative distance
    return Math.sqrt(d); //Actual distance, less performant
};
export { distanceBetweenPoints };

const groupAsSingle = (json) => {
    if (!json.list[0]) return [];
    return json.list.map((poi) => {
        return {
            data: [poi],
            x: poi.x,
            y: poi.y
        };
    });
};
export { groupAsSingle };

const getZoomFromTileKey = (tilekey) => {
    return tilekey.substring(2, 3);
};
export { getZoomFromTileKey };

// ΔVΔVΔ: Expects an array of poi locations
// returns empty data because too much to display
const groupPoiByState = (json, mapped = {}, format = 'array') => {
    // const mapped = {};
    //json = [
    //{ "NAME", "ADDRESS", "CITY", "STATE", "ZIP", "COUNTY", "TELEPHONE", "x", "y" }
    //]

    for (const poi of json) {
        // console.log('mapped, poi: ', mapped, poi);
        const key = poi.STATE ? poi.STATE : poi.attributes.STATE;
        if (!mapped[key]) {
            const found = statesList.find((st) => {
                return st.abbr == key;
            });
            if (found) {
                mapped[key] = { val: 0, x: found.center[0], y: found.center[1], name: key, data: [] };
            } else {
                mapped[key] = { val: 0, x: 0, y: 0, name: '', data: [] };
            }
        }
        mapped[key].val++;
        mapped[key].data.push(poi);
    }
    return format == 'array' ? Object.values(mapped) : mapped;
};
export { groupPoiByState };

const factorial = (num) => {
    if (num < 0) return -1;
    if (num == 0) return 1;
    return num * factorial(num - 1);
};
export { factorial };

const triangleNumber = (num) => {
    if (num < 0) return -1;
    if (num == 0) return 0;
    return num + triangleNumber(num - 1);
};

const chunkArray = (array, size) => {
    const chunked_arr = [];
    const numOfChild = Math.ceil(array.length / size); // Round up to the nearest integer
    for (let i = 0; i < numOfChild; i++) {
        chunked_arr.push(array.splice(0, size));
    }
    return chunked_arr;
};
export { chunkArray };

const findCenter = (coords) => {
    if (coords.length == 1) return coords[0]; //only one set of coords is a point
    return getCenter(boundingExtent(coords)); //find point in center of all points provided
};
export { findCenter };

const mapCountyTopoJson = (json_url) => {
    fetch(json_url)
        .then((res) => res.json())
        .then((json) => {
            const objkey = Object.keys(json.objects)[0];
            const geo = json.objects[objkey].geometries;
            const objects = {
                counties: {
                    geometries: geo.map((item) => {
                        const { COUNTY, NAME, STATE } = item.properties;
                        return {
                            arcs: item.arcs,
                            type: item.type,
                            properties: {
                                FIPS: STATE + COUNTY,
                                NAME: NAME,
                                STATE_NAME: getStateBy(STATE)?.label
                            }
                        };
                    }),
                    type: 'GeometryCollection'
                }
            };

            const output = {
                type: json.type,
                arcs: json.arcs,
                transform: json.transform,
                objects: objects
            };
        });
};
export { mapCountyTopoJson };

const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

const tableDataMappingOrdered = (data, columns = []) => {
    return data
        .map((row) => {
            let item = {}; //whitelistKeys.length ? pick(row, whitelistKeys) : row;
            item['GEO LEVEL'] = capitalizeFirstLetter(row.GeoLevel);
            item['GEO COMPARISON'] = capitalizeFirstLetter(row.Comparison);
            for (let key in columns) {
                let props = columns[key];
                if (!props?.hide) {
                    // let key = columns[ind];
                    item[props.headerName] = row[key];
                }
            }
            delete item['Geo Unit'];
            delete item['Comparison'];
            const foundState = statesList.find((st) => {
                return st.id == row.ST;
            });
            if (foundState) {
                item.STATE = foundState.label;
                item.ST_ABBR = foundState.abbr;
            }
            item.STCNTY = row.FIPS.substring(0, 5);
            const foundCounty = countiesList.find((cnty) => {
                return cnty.CC + '' === item.STCNTY + '';
            });
            if (foundCounty) {
                var reg = new RegExp('&#39;', 'g');
                item.COUNTY = foundCounty.CN.replace(reg, "'");
            }
            return item;
        })
        .sort((a, b) => parseInt(a.FIPS) - parseInt(b.FIPS));
};
export { tableDataMappingOrdered };

const tableDataMapping = (data, whitelistKeys = []) => {
    return data
        .map((row) => {
            let item = whitelistKeys.length ? pick(row, whitelistKeys) : row;
            item.id = row.OBJECTID || row.GRASPID;
            item.GeoLevel = capitalizeFirstLetter(item.GeoLevel);
            item.Comparison = capitalizeFirstLetter(item.Comparison);
            const foundState = statesList.find((st) => {
                return st.id == item.ST;
            });
            if (foundState) {
                item.STATE_NAME = foundState.label;
                item.STATE_ABBR = foundState.abbr;
            }
            item.COUNTY_FIPS = item.FIPS.substring(0, 5);
            const foundCounty = countiesList.find((cnty) => {
                return cnty.CC + '' === item.COUNTY_FIPS + '';
            });
            if (foundCounty) {
                //&#39;
                var reg = new RegExp('&#39;', 'g');
                item.COUNTY_NAME = foundCounty.CN.replace(reg, "'");
            }
            return item;
        })
        .sort((a, b) => parseInt(a.FIPS) - parseInt(b.FIPS));
};
export { tableDataMapping };

const readErrorMesssage = (message, err) => {
    if (message) return message; //ΔVΔVΔ: If there is a message explicitly sent then use that
    let serr = String(err?.err || err); //ΔVΔVΔ: In case send as embeded object
    return serr;
};
export { readErrorMesssage };

//helper function to convert an extent drawn from:
//https://www.keene.edu/campus/maps/tool/
/*
example input:
-132.1875000, 48.0093777
-106.9921830, 47.9636190
-107.0361283, 33.2582183
-131.5136728, 33.2232081
*/
const convertDrawnOnlineExtent = (str) => {
    var arr = str.split(`\n`).map((pointstr) => pointstr.replace(' ', '').split(','));
    var pointA = fromLonLat(arr[0]);
    var pointB = fromLonLat(arr[2]);
    // return [...pointA, ...pointB];
    var latlonext = boundingExtent([arr[0], arr[1]]);
    return transformExtent(latlonext, getExtent('EPSG:4326'), getExtent('EPSG:3857'));
};
